import { forwardRef, useImperativeHandle, useState } from 'react';
import Select from 'react-select';

import {
  InputContainer,
  UpdateLabel,
  UpdateInput,
  UpdateTextarea,
  UpdateCheckbox,
  UpdateError
} from './dynamic-product-fields.styles';

const DynamicProductFields = forwardRef(({ fields, values, setValues }, ref) => {
  const [errors, setErrors] = useState({});

  useImperativeHandle(ref, () => ({
    validateFields: () => {
      const newErrors = {};
      fields.forEach(field => {
        const val = values[field.key];
        const isEmpty = val === undefined || val === null || val === '';
        if (field.required && isEmpty) {
          newErrors[field.key] = `${field.label} is required`;
        }
      });
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }
  }));

  const handleChange = (key, value) => {
    setValues(prev => ({ ...prev, [key]: value }));
  };

  const renderField = (field) => {
    const value = values[field.key] ?? '';
    const error = errors[field.key];

    switch (field.type) {
      case 'text':
        return (
          <InputContainer key={field.key}>
            <UpdateLabel>{field.label}{field.required && ' *'}</UpdateLabel>
            <UpdateInput
              value={value}
              onChange={e => handleChange(field.key, e.target.value)}
            />
            {error && <UpdateError>{error}</UpdateError>}
          </InputContainer>
        );

      case 'textarea':
        return (
          <InputContainer key={field.key}>
            <UpdateLabel>{field.label}{field.required && ' *'}</UpdateLabel>
            <UpdateTextarea
              value={value}
              onChange={e => handleChange(field.key, e.target.value)}
            />
            {error && <UpdateError>{error}</UpdateError>}
          </InputContainer>
        );

      case 'select':
        return (
          <InputContainer key={field.key}>
            <UpdateLabel>{field.label}{field.required && ' *'}</UpdateLabel>
            <Select
              value={
                Array.isArray(field.options)
                  ? field.options.find(opt => opt.value === value) || null
                  : null
              }
              onChange={selected => handleChange(field.key, selected?.value ?? '')}
              options={field.options || []}
              isClearable
            />
            {error && <UpdateError>{error}</UpdateError>}
          </InputContainer>
        );

      case 'boolean':
        return (
          <InputContainer key={field.key}>
            <UpdateCheckbox
              type="checkbox"
              checked={!!value}
              onChange={e => handleChange(field.key, e.target.checked)}
            />
            <UpdateLabel>{field.label}</UpdateLabel>
          </InputContainer>
        );

      case 'number':
        return (
          <InputContainer key={field.key}>
            <UpdateLabel>{field.label}{field.required && ' *'}</UpdateLabel>
            <UpdateInput
              type="number"
              value={value}
              onChange={e => handleChange(field.key, e.target.value)}
            />
            {error && <UpdateError>{error}</UpdateError>}
          </InputContainer>
        );

      case 'date':
        return (
          <InputContainer key={field.key}>
            <UpdateLabel>{field.label}{field.required && ' *'}</UpdateLabel>
            <UpdateInput
              type="date"
              value={value}
              onChange={e => handleChange(field.key, e.target.value)}
            />
            {error && <UpdateError>{error}</UpdateError>}
          </InputContainer>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {fields
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
        .map(renderField)}
    </>
  );
});

export default DynamicProductFields;